import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { toast } from "react-toastify";
import {
    fetchCurrentExchangeRate,
    fetchSuggestedExchangeRate,
    fetchExchangeRateHistory,
    setExchangeRate,
} from "../../services/exchangeRateService";
import styles from "../shifts/shifts.module.scss";

const formatRate = (rate) => (rate === null || rate === undefined || isNaN(rate) ? "-" : `J$${Number(rate).toFixed(2)}`);
const formatWhen = (date) => (date ? new Date(date).toLocaleString() : "-");

const AdminExchangeRate = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [suggested, setSuggested] = useState(null);
    const [suggesting, setSuggesting] = useState(false);
    const [rateInput, setRateInput] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadSuggestion = async () => {
        setSuggesting(true);
        const res = await fetchSuggestedExchangeRate();
        const rate = res?.data?.rate || null;
        setSuggested(rate);
        // Only pre-fills the form if the admin hasn't already started typing
        // their own value - never overwrites an in-progress edit.
        setRateInput((prev) => (prev === null ? rate : prev));
        setSuggesting(false);
    };

    const loadAll = async () => {
        setLoading(true);
        const [currentRes, historyRes] = await Promise.all([fetchCurrentExchangeRate(), fetchExchangeRateHistory()]);
        setCurrent(currentRes?.data || null);
        setHistory(historyRes?.data || []);
        setLoading(false);
        loadSuggestion();
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        if (!rateInput || rateInput <= 0) {
            toast.error("Enter a valid rate");
            return;
        }
        setSaving(true);
        try {
            const res = await setExchangeRate(dispatch, rateInput, suggested);
            if (res?.success) {
                toast.success("Exchange rate updated");
                setRateInput(null);
                loadAll();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-sync" />
                    </div>
                    <div>
                        <h2>Exchange Rate</h2>
                        <p className={styles.subtitle}>JMD per 1 USD - used to convert every JMD cash/wire entry back to USD</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading exchange rate...</span>
                </div>
            ) : (
                <>
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-money-bill" />
                            </div>
                            <div>
                                <div className={styles.statValue}>{formatRate(current?.rate)}</div>
                                <div className={styles.statLabel}>Current Rate</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-clock" />
                            </div>
                            <div>
                                <div className={styles.statValue} style={{ fontSize: "14px" }}>{formatWhen(current?.createdAt)}</div>
                                <div className={styles.statLabel}>Last Updated</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-user" />
                            </div>
                            <div>
                                <div className={styles.statValue} style={{ fontSize: "14px" }}>{current?.setByName || "-"}</div>
                                <div className={styles.statLabel}>Set By</div>
                            </div>
                        </div>
                    </div>

                    {!current && (
                        <div className={styles.sectionCard} style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                            <p className={styles.shiftSub} style={{ color: "#92400e" }}>
                                No exchange rate has been set yet - JMD cash and wire payments will be blocked everywhere until one is set below.
                            </p>
                        </div>
                    )}

                    <div className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <h3>
                                <i className="pi pi-pencil" />
                                Set New Rate
                            </h3>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            <p className={styles.shiftSub}>
                                {suggesting
                                    ? "Fetching a live suggestion..."
                                    : suggested
                                      ? `Live suggestion: J$${suggested.toFixed(2)} per USD. Adjust if needed before saving.`
                                      : "No live suggestion available right now - enter the rate manually."}
                            </p>
                            <div className={styles.metaGrid}>
                                <div className={styles.metaBox}>
                                    <div className={styles.metaLabel}>Rate (JMD per 1 USD)</div>
                                    <InputNumber value={rateInput} onValueChange={(e) => setRateInput(e.value)} minFractionDigits={2} maxFractionDigits={4} min={0.01} placeholder="e.g. 155.50" />
                                </div>
                            </div>
                            <div className={styles.actionRow} style={{ justifyContent: "flex-start" }}>
                                <Button
                                    label={saving ? "Saving..." : "Save Rate"}
                                    icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    className={styles.primaryButton}
                                    onClick={handleSave}
                                    disabled={saving}
                                />
                                <Button
                                    label="Refresh Suggestion"
                                    icon={suggesting ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                                    className={styles.secondaryButton}
                                    onClick={() => {
                                        setSuggested(null);
                                        setRateInput(null);
                                        loadSuggestion();
                                    }}
                                    disabled={suggesting}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionCard} style={{ maxWidth: "none" }}>
                        <div className={styles.sectionCardHeader}>
                            <h3>
                                <i className="pi pi-history" />
                                Rate History
                            </h3>
                        </div>
                        {history.length === 0 ? (
                            <div className={styles.emptyBreaks}>No rate changes recorded yet.</div>
                        ) : (
                            history.map((entry) => (
                                <div className={styles.breakRow} key={entry._id}>
                                    <strong>{formatRate(entry.rate)}</strong>
                                    <span>
                                        {entry.setByName || "Unknown"} - {formatWhen(entry.createdAt)}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminExchangeRate;
